import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { TenantDTO, TenantDTOTenantStatusEnum, UserDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { loadMe, MeInitializationResult, useTenant } from 'probonio-shared-ui/module/me';
import { AdminTenantAccessData, flattenSwitchableTenants, mayReadAllTenants } from 'probonio-shared-ui/module/me/permissionSelectors';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationType, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useSwitchTenant } from './useSwitchTenant';

export const TENANT_ID_SEARCH_PARAM = 'tenantId';

async function mayOpenTenant(
  user: UserDTO | undefined,
  adminTenantAccessData: AdminTenantAccessData | undefined,
  tenantId: string,
): Promise<boolean> {
  if (flattenSwitchableTenants(adminTenantAccessData?.switchableTenants ?? []).some(tenant => tenant.id === tenantId)) {
    return true;
  }
  if (!mayReadAllTenants(user)) {
    return false;
  }
  // Global readers may open any tenant, so only the tenant itself can tell whether the id is valid.
  try {
    const tenant = (await apis.tenants.getTenant({ tenantId })).data;
    return tenant.tenantStatus !== TenantDTOTenantStatusEnum.Deleted;
  } catch {
    return false;
  }
}

/**
 * The tenant named by `?tenantId=` in the first of `urls` that has one, for `loadMe` to open as `preferredTenantId`:
 * a global reader without tenants of their own would otherwise be turned away before `useLinkedTenant` ever sees the
 * parameter. `loadMe` ignores ids the user may not open.
 */
export function linkedTenantIdFromUrl(...urls: (string | null)[]): string | undefined {
  for (const url of urls) {
    const tenantId = url && new URL(url, window.location.origin).searchParams.get(TENANT_ID_SEARCH_PARAM);
    if (tenantId) {
      return tenantId;
    }
  }
  return undefined;
}

/**
 * Moves the manager to `tenantId` on behalf of the user, as opposed to following a link: the tenant becomes the last
 * selected one and is opened without another access check, since the tenant switcher only offers what the user may
 * open.
 */
export function useManualTenantSwitch(): (tenantId: string) => void {
  const navigate = useNavigate();

  return useCallback(
    (tenantId: string) => {
      const search = new URLSearchParams({ [TENANT_ID_SEARCH_PARAM]: tenantId }).toString();
      const state: ManualTenantSwitchState = { manualTenantSwitch: true };
      navigate({ pathname: '/', search }, { state });
    },
    [navigate],
  );
}

interface ManualTenantSwitchState {
  manualTenantSwitch: true;
}

function isManualTenantSwitchState(state: unknown): state is ManualTenantSwitchState {
  return typeof state === 'object' && state !== null && 'manualTenantSwitch' in state;
}

/** The open tenant does not exist (anymore). Other errors (network, 5xx) say nothing about that. */
function isTenantGone(tenant: TenantDTO | undefined, error: unknown): boolean {
  return (
    (error instanceof AxiosError && (error.response?.status === 403 || error.response?.status === 404)) ||
    tenant?.tenantStatus === TenantDTOTenantStatusEnum.Deleted
  );
}

/**
 * Keeps the open tenant in the URL as `?tenantId=`, so the address bar is a link to it - the link-out from the admin
 * portal is one as well. The URL leads: when it names another tenant than the open one - a link was followed, or the
 * browser went back to a page of another tenant - that tenant is opened, if the user may. Only users who can switch
 * tenants carry the parameter; for everybody else there is only one tenant to link to.
 */
export function useLinkedTenant(): void {
  const { t } = useTranslation('navigationModule');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const switchToTenant = useSwitchTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();
  const { user, adminTenantAccessData, currentTenantId, initializationResult } = useAppSelector(state => state.me);
  const { tenant, error: tenantError } = useTenant();
  // The URL parameter last acted upon; `undefined` until the first one after initialization.
  const handledTenantId = useRef<string | null>(undefined);
  // The tenant the URL names that is still on its way to being opened - the URL must keep naming it until then.
  const pendingTenantId = useRef<string>(undefined);
  // A tenant that turned out to be gone, which must not be written back into the URL.
  const droppedTenantId = useRef<string>(undefined);
  // The linked tenant `loadMe` already opened unchecked - only loading it tells whether it exists.
  const uncheckedTenantId = useRef<string>(undefined);
  // Bumped when one of the refs above changes in a way the URL has to follow.
  const [urlSyncRequest, requestUrlSync] = useReducer((count: number) => count + 1, 0);

  const requestedTenantId = searchParams.get(TENANT_ID_SEARCH_PARAM);
  const isInitialized = initializationResult === MeInitializationResult.SUCCESS;
  const carriesTenantId = !!adminTenantAccessData?.canSwitch || mayReadAllTenants(user);
  const isManualSwitch = navigationType === NavigationType.Push && isManualTenantSwitchState(location.state);

  // URL -> tenant: open the tenant a new `?tenantId=` names.
  useEffect(() => {
    if (!isInitialized || !currentTenantId || handledTenantId.current === requestedTenantId) {
      return;
    }
    const isFirstVisit = handledTenantId.current === undefined;
    handledTenantId.current = requestedTenantId;
    // whatever was pending before, the URL has moved on
    pendingTenantId.current = undefined;

    if (!requestedTenantId) {
      return;
    }
    if (requestedTenantId === currentTenantId) {
      if (isFirstVisit) {
        // linkedTenantIdFromUrl got it to loadMe, which already opened it
        uncheckedTenantId.current = requestedTenantId;
      }
      return;
    }

    pendingTenantId.current = requestedTenantId;
    if (isManualSwitch) {
      switchToTenant(requestedTenantId);
      return;
    }
    void mayOpenTenant(user, adminTenantAccessData, requestedTenantId).then(mayOpen => {
      if (pendingTenantId.current !== requestedTenantId) {
        // the URL moved on while the check ran - the newer one decides
        return;
      }
      if (!mayOpen) {
        pendingTenantId.current = undefined;
        enqueueSnackbar(t('changeTenantDialog.linkedTenantNotAccessible'), { variant: 'warning' });
        requestUrlSync();
        return;
      }
      // following a link must not move where the next login starts
      switchToTenant(requestedTenantId, { remember: false });
    });
  }, [adminTenantAccessData, currentTenantId, enqueueSnackbar, isInitialized, isManualSwitch, requestedTenantId, switchToTenant, t, user]);

  // tenant -> URL: let `?tenantId=` name the open tenant. Runs after the effect above, which marks what it opens as pending.
  useEffect(() => {
    if (!isInitialized || !currentTenantId) {
      return;
    }
    if (pendingTenantId.current) {
      if (pendingTenantId.current !== currentTenantId) {
        return;
      }
      pendingTenantId.current = undefined;
    }
    if (currentTenantId !== droppedTenantId.current) {
      droppedTenantId.current = undefined;
    }
    const urlTenantId = carriesTenantId && !droppedTenantId.current ? currentTenantId : null;
    if (requestedTenantId === urlTenantId) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    if (urlTenantId) {
      params.set(TENANT_ID_SEARCH_PARAM, urlTenantId);
    } else {
      params.delete(TENANT_ID_SEARCH_PARAM);
    }
    // the URL only catches up with the open tenant here, there is nothing to follow
    handledTenantId.current = urlTenantId;
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true, state: location.state });
  }, [
    carriesTenantId,
    currentTenantId,
    isInitialized,
    location.hash,
    location.pathname,
    location.state,
    navigate,
    requestedTenantId,
    searchParams,
    urlSyncRequest,
  ]);

  // The open tenant may not exist (anymore) - fall back to another one. That is only worth a warning when a link to
  // it was just followed; a tenant opened earlier and deleted since is dropped quietly. Other errors keep it, so a
  // reload still returns to the tenant.
  const isOpenTenantGone = isTenantGone(tenant, tenantError);
  useEffect(() => {
    if (isOpenTenantGone && currentTenantId && currentTenantId !== droppedTenantId.current) {
      if (uncheckedTenantId.current === currentTenantId) {
        enqueueSnackbar(t('changeTenantDialog.linkedTenantNotAccessible'), { variant: 'warning' });
      }
      uncheckedTenantId.current = undefined;
      droppedTenantId.current = currentTenantId;
      requestUrlSync();
      void dispatch(loadMe({ discardTenantId: currentTenantId }));
    }
  }, [currentTenantId, dispatch, enqueueSnackbar, isOpenTenantGone, t]);
}
